import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { explainFunctionError, functionErrorResponse, useSupabase, useTenant } from "@how/app-kit";
import { z } from "zod";
import { env } from "../../lib/env";
import type { StoreOptions } from "./shop-rules";
import { DISPLAY_LOCALE } from "@how/ui";

/** The member's half of the store (ADR 0047): the catalog, placing and paying for an order, and their orders. */

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

const variantSchema = z.object({
  id: z.string(),
  name: z.string(),
  priceCents: z.number(),
  available: z.boolean(),
  left: z.number().nullable().optional(),
});
export const productSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  currency: z.string(),
  /** Absent from a database older than store step 2: treated as either. */
  fulfilment: z.enum(["pickup", "ship", "either"]).optional().default("either"),
  image: z.object({ path: z.string(), alt: z.string() }).nullable(),
  variants: z
    .array(variantSchema)
    .nullable()
    .transform((v) => v ?? []),
});
export type ShopProduct = z.infer<typeof productSchema>;
export type ShopVariant = z.infer<typeof variantSchema>;

const shipToSchema = z.object({
  name: z.string().optional(),
  line1: z.string().optional(),
  line2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().optional(),
});
export type ShipTo = z.infer<typeof shipToSchema>;

const orderSchema = z.object({
  id: z.string(),
  number: z.number(),
  status: z.enum(["pending_payment", "paid", "fulfilled", "cancelled"]),
  totalCents: z.number(),
  currency: z.string(),
  refundedCents: z.number(),
  createdAt: z.string(),
  paidAt: z.string().nullable(),
  fulfilledAt: z.string().nullable(),
  fulfilment: z.enum(["pickup", "ship"]).optional().default("pickup"),
  shippingCents: z.number().optional().default(0),
  taxCents: z.number().optional().default(0),
  shipTo: shipToSchema.nullable().optional().default(null),
  carrier: z.string().nullable().optional().default(null),
  trackingNumber: z.string().nullable().optional().default(null),
  /** An event's add-ons: the event they are for, handed over there (ADR 0047 step 3). */
  forEvent: z.string().nullable().optional().default(null),
  items: z
    .array(
      z.object({
        product: z.string(),
        variant: z.string(),
        quantity: z.number(),
        unitPriceCents: z.number(),
      }),
    )
    .nullable()
    .transform((i) => i ?? []),
});
export type MyOrder = z.infer<typeof orderSchema>;

/** The public address of a product image in the church's media area. */
export function imageUrl(path: string): string {
  const encoded = path.split("/").map(encodeURIComponent).join("/");
  return `${env.VITE_SUPABASE_URL.replace(/\/$/, "")}/storage/v1/object/public/how-public/${encoded}`;
}

export function formatPrice(cents: number, currency: string): string {
  return new Intl.NumberFormat(DISPLAY_LOCALE, {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

const optionsSchema = z.object({
  mode: z.enum(["how", "shopify", "none"]).optional().default("how"),
  shopifyUrl: z.string().nullable().optional().default(null),
  pickup: z.boolean(),
  pickupNote: z.string().nullable(),
  shipping: z.boolean(),
  shippingRateCents: z.number(),
  freeShippingOverCents: z.number().nullable(),
  shipCountries: z.array(z.string()),
  tax: z.boolean(),
});

export function useStoreOptions(enabled = true) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["shop-options", tenantId],
    enabled: enabled && tenantId !== null,
    queryFn: async (): Promise<StoreOptions> => {
      const { data, error } = await supabase.rpc("store_options", { p_tenant_id: tenantId ?? "" });
      if (error) throw error;
      return optionsSchema.parse(data);
    },
  });
}

export function useCatalog() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["shop-catalog", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<ShopProduct[]> => {
      const { data, error } = await supabase.rpc("store_catalog", { p_tenant_id: tenantId ?? "" });
      if (error) throw error;
      return z.array(productSchema).parse(data);
    },
  });
}

export function useMyOrders() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["my-orders", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<MyOrder[]> => {
      const { data, error } = await supabase.rpc("my_orders", { p_tenant_id: tenantId ?? "" });
      if (error) throw error;
      return z.array(orderSchema).parse(data);
    },
  });
}

export interface CartLine {
  readonly variantId: string;
  readonly quantity: number;
}

async function openCheckout(
  supabase: ReturnType<typeof useSupabase>,
  tenantId: string,
  orderId: string,
): Promise<string> {
  const result = await supabase.functions.invoke<{ url: string }>("stripe-checkout", {
    body: { tenant_id: tenantId, order_id: orderId },
  });
  if (result.error) {
    throw await explainFunctionError(result.error, functionErrorResponse(result.error));
  }
  return result.data?.url ?? "";
}

/**
 * Place the order (the database takes the prices and holds the stock), then open Stripe Checkout on the church's
 * own account for it. The browser goes to Stripe; the webhook marks the order paid.
 */
export function usePlaceOrder() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      readonly lines: readonly CartLine[];
      readonly fulfilment: "pickup" | "ship";
    }): Promise<string> => {
      const placed = await supabase.rpc("place_order", {
        p_tenant_id: tenantId ?? "",
        p_items: input.lines.map((l) => ({ variantId: l.variantId, quantity: l.quantity })),
        p_fulfilment: input.fulfilment,
      });
      if (placed.error) throw new Error(placed.error.message);
      await client.invalidateQueries({ queryKey: ["my-orders", tenantId] });
      return openCheckout(supabase, tenantId ?? "", placed.data);
    },
    onSuccess: (url) => {
      if (url !== "") window.location.assign(url);
    },
  });
}

/** Pay for an order placed earlier and not paid yet. */
export function usePayOrder() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: (orderId: string) => openCheckout(supabase, tenantId ?? "", orderId),
    onSuccess: (url) => {
      if (url !== "") window.location.assign(url);
    },
  });
}

export function useCancelMyOrder() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (orderId: string) => {
      const { error } = await supabase.rpc("cancel_my_order", {
        p_tenant_id: tenantId ?? "",
        p_order_id: orderId,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["my-orders", tenantId] });
      await client.invalidateQueries({ queryKey: ["shop-catalog", tenantId] });
    },
  });
}
